import { apiClient } from '@/lib/api-client';
import i18n from '@/i18n';

/**
 * Oflayn sotuv navbati — IndexedDB'da saqlanadi.
 * Naqd/karta/aralash sotuvlar internet uzilganda navbatga olinadi va
 * tarmoq qaytganda avtomatik yuboriladi. Murabaha sotuvlar hech qachon
 * navbatga olinmaydi (ular oflayn ishlamaydi).
 */

const DB_NAME = 'hisob-pos-offline';
const DB_VERSION = 1;
const QUEUE_STORE = 'sales-queue';
const KV_STORE = 'kv';

export const QUEUE_CHANGED_EVENT = 'hisob-pos-queue-changed';

export interface QueuedSale {
  id: string;
  queued_at: string;
  /** `POST /sales` payloadi (StoreSaleRequest shaklida, to'liq) */
  body: Record<string, any>;
  /** Ixtiyoriy mijoz ma'lumoti — yuborishda avval upsert qilinadi */
  customerInfo?: { full_name: string; phone: string } | null;
}

export function notifyQueueChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(QUEUE_CHANGED_EVENT));
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(QUEUE_STORE)) db.createObjectStore(QUEUE_STORE, { keyPath: 'id' });
      if (!db.objectStoreNames.contains(KV_STORE)) db.createObjectStore(KV_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  fn: (s: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return openDb().then(
    db =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
        t.oncomplete = () => db.close();
      })
  );
}

function uuid(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export async function enqueueSale(
  entry: Pick<QueuedSale, 'body'> & Partial<QueuedSale>
): Promise<QueuedSale> {
  const record: QueuedSale = {
    id: entry.id ?? uuid(),
    queued_at: new Date().toISOString(),
    body: entry.body,
    customerInfo: entry.customerInfo ?? null,
  };
  await tx(QUEUE_STORE, 'readwrite', s => s.put(record));
  notifyQueueChanged();
  return record;
}

export async function getQueuedSales(): Promise<QueuedSale[]> {
  const rows = await tx<QueuedSale[]>(QUEUE_STORE, 'readonly', s => s.getAll() as IDBRequest<QueuedSale[]>);
  return (rows ?? []).sort((a, b) => a.queued_at.localeCompare(b.queued_at));
}

export async function removeQueuedSale(id: string): Promise<void> {
  await tx(QUEUE_STORE, 'readwrite', s => s.delete(id));
  notifyQueueChanged();
}

export async function getQueueCount(): Promise<number> {
  const rows = await tx(QUEUE_STORE, 'readonly', s => s.count());
  return Number(rows ?? 0);
}

let flushing = false;

/** Navbatdagi sotuvlarni ketma-ket yuboradi; birinchisida xato bo'lsa to'xtaydi. */
export async function flushSalesQueue(): Promise<{ sent: number; failed: number }> {
  if (flushing) return { sent: 0, failed: 0 };
  flushing = true;
  let sent = 0;
  let failed = 0;
  try {
    const sales = await getQueuedSales();
    for (const entry of sales) {
      try {
        const body = { ...entry.body };
        let customerId = (body.customer_id as number | null) || null;

        if (!customerId && entry.customerInfo) {
          // `POST /customers` is an upsert by phone (StoreCustomerController) —
          // no separate lookup step needed.
          const { data: customer, error: customerError } = await apiClient.POST('/customers', {
            body: {
              full_name: entry.customerInfo.full_name,
              phone: entry.customerInfo.phone,
              source: 'pos_sale',
            } as never,
          });
          if (customerError) throw new Error(i18n.t('misc_queue.customer_error'));
          customerId = (customer as { data: { id: number } }).data.id;
          body.customer_id = customerId;
        }

        // client_reference makes the retry idempotent: SaleCreationService
        // returns the existing sale instead of creating a duplicate.
        const { error: saleError } = await apiClient.POST('/sales', {
          body: { ...body, client_reference: entry.id } as never,
        });
        if (saleError) throw new Error(i18n.t('misc_queue.sale_error'));

        // Customer totals are computed by the backend from real sale
        // records, not maintained via a client-side increment call.

        await removeQueuedSale(entry.id);
        sent++;
      } catch {
        failed++;
        break; // tartibni saqlash uchun birinchi xatoda to'xtaymiz
      }
    }
  } finally {
    flushing = false;
    if (sent > 0) notifyQueueChanged();
  }
  return { sent, failed };
}

/** Mahsulotlar ro'yxatini oflayn foydalanish uchun keshlash */
export async function cacheProducts(merchantId: string | null, products: any[]): Promise<void> {
  if (!merchantId) return;
  await tx(KV_STORE, 'readwrite', s => s.put({ products, cached_at: new Date().toISOString() }, `products:${merchantId}`));
}

export async function getProductsCache(merchantId: string | null): Promise<any[] | null> {
  if (!merchantId) return null;
  const row = await tx<any>(KV_STORE, 'readonly', s => s.get(`products:${merchantId}`));
  return row?.products ?? null;
}
